import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import '../styles/components/general-splash-v3.css';

const SPLASH_DEFAULT_DURATION_MS = 10000;
const SPLASH_EXTENDED_DURATION_MS = 10000;
const EXIT_DELAY_MS = 420;

const PARKS = [
  'Disneyland Paris',
  'Europa-Park',
  'Parc Astérix',
  'Efteling',
  'Universal Orlando',
  'Magic Kingdom'
];

const WAIT_TIMES = [
  {
    name: 'Big Thunder Mountain',
    value: '15',
    status: 'min live',
    tone: 'live'
  },
  {
    name: 'KONDAA',
    value: '35',
    status: 'stable',
    tone: 'stable'
  },
  {
    name: 'Jurassic World VelociCoaster',
    value: '05',
    status: 'court',
    tone: 'short'
  },
  {
    name: 'Symbolica',
    value: '20',
    status: 'fluide',
    tone: 'smooth'
  }
];

const FEATURE_CARDS = [
  {
    label: 'Météo',
    title: 'Anticipez la journée',
    meta: 'Pluie, vent, ressenti'
  },
  {
    label: 'Cartes',
    title: 'Repérez les zones utiles',
    meta: 'Attractions, services, hôtels'
  },
  {
    label: 'Favoris',
    title: 'Suivez vos parcs',
    meta: 'Accès direct aux infos'
  },
  {
    label: 'Alertes',
    title: 'Prochainement',
    meta: 'Notifications utiles à venir'
  }
];

const FINAL_CARDS = [
  {
    kicker: 'Attentes',
    title: 'En direct',
    body: 'Temps réel sur les attractions populaires'
  },
  {
    kicker: 'Favoris',
    title: 'Parcs suivis',
    body: 'Vos destinations prioritaires au premier plan'
  },
  {
    kicker: 'Sans pub',
    title: 'Sans tracking pub',
    body: 'Une expérience lisible et respectueuse'
  }
];

const FOOTER_MESSAGES = [
  { max: 12, text: 'Initialisation de Magic Park Times' },
  { max: 24, text: 'Chargement des parcs populaires' },
  { max: 36, text: 'Synchronisation des temps d’attente' },
  { max: 48, text: 'Préparation des cartes et favoris' },
  { max: 60, text: 'Préparation des alertes à venir' },
  { max: 72, text: 'Alignement des minutes avec la réalité' },
  { max: 82, text: 'Rangement des files d’attente par ordre de patience' },
  { max: 90, text: 'Polissage des paillettes numériques' },
  { max: 96, text: 'Dernier tour de manège avant ouverture' },
  { max: 100, text: 'Promis, on ne fait pas exprès de faire durer le suspense' }
];

const getSceneFromProgress = (progress) => {
  if (progress < 16) return 'brand';
  if (progress < 38) return 'parks';
  if (progress < 64) return 'live';
  if (progress < 84) return 'tools';
  return 'final';
};

const getFooterMessageFromProgress = (progress) => {
  const matchedMessage = FOOTER_MESSAGES.find((message) => progress <= message.max);
  return matchedMessage?.text || FOOTER_MESSAGES[FOOTER_MESSAGES.length - 1].text;
};

const normalizeDuration = (duration, fallback) => {
  const parsedDuration = Number(duration);
  return Number.isFinite(parsedDuration) && parsedDuration > 0
    ? parsedDuration
    : fallback;
};

const dispatchSplashState = (active) => {
  if (typeof window === 'undefined') return;

  window.__MPT_SPLASH_ACTIVE = active;
  window.dispatchEvent(new window.CustomEvent('mpt:splash-state', {
    detail: { active, source: 'general-v3' }
  }));
};

const usePrefersReducedMotion = () => {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return false;
    }

    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return undefined;
    }

    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handleChange = (event) => setPrefersReducedMotion(event.matches);

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }

    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  return prefersReducedMotion;
};

const GeneralSplashScreenV3 = ({
  onComplete,
  durationMs = SPLASH_DEFAULT_DURATION_MS,
  serviceWorkerUpdateDurationMs = SPLASH_EXTENDED_DURATION_MS
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [isExiting, setIsExiting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [activeScene, setActiveScene] = useState('brand');
  const canvasRef = useRef(null);
  const updatePendingRef = useRef(
    typeof window !== 'undefined'
      ? Boolean(window.__MPT_SW_UPDATE_PENDING_DURING_SPLASH)
      : false
  );
  const prefersReducedMotion = usePrefersReducedMotion();
  const baseDurationMs = useMemo(
    () => normalizeDuration(durationMs, SPLASH_DEFAULT_DURATION_MS),
    [durationMs]
  );
  const extendedDurationMs = useMemo(
    () => Math.max(
      baseDurationMs,
      normalizeDuration(serviceWorkerUpdateDurationMs, SPLASH_EXTENDED_DURATION_MS)
    ),
    [baseDurationMs, serviceWorkerUpdateDurationMs]
  );

  const statusText = useMemo(() => {
    if (activeScene === 'parks') return 'Présentation des parcs populaires';
    if (activeScene === 'live') return 'Temps d’attente en temps réel';
    if (activeScene === 'tools') return 'Découverte des outils de visite';
    if (activeScene === 'final') return 'Magic Park Times est prêt';
    return 'Découverte de Magic Park Times';
  }, [activeScene]);
  const footerMessage = useMemo(
    () => getFooterMessageFromProgress(progress),
    [progress]
  );

  const finishSplash = useCallback(() => {
    setProgress(100);
    setActiveScene('final');
    setIsExiting(true);
    dispatchSplashState(false);

    window.setTimeout(() => {
      if (typeof document !== 'undefined') {
        document.documentElement.classList.remove('mpt-splash-v3-active');
        document.body.classList.remove('mpt-splash-v3-active');
      }
      setIsVisible(false);
      if (onComplete) {
        onComplete();
      }
    }, EXIT_DELAY_MS);
  }, [onComplete]);

  useEffect(() => {
    dispatchSplashState(true);
    if (typeof document !== 'undefined') {
      document.documentElement.classList.add('mpt-splash-v3-active');
      document.body.classList.add('mpt-splash-v3-active');
    }

    return () => {
      dispatchSplashState(false);
      if (typeof document !== 'undefined') {
        document.documentElement.classList.remove('mpt-splash-v3-active');
        document.body.classList.remove('mpt-splash-v3-active');
      }
    };
  }, []);

  useEffect(() => {
    if (prefersReducedMotion) {
      setActiveScene('final');
    }
  }, [prefersReducedMotion]);

  useEffect(() => {
    let animationFrameId;
    let finishTimerId;
    let isDone = false;
    const startedAt = performance.now();

    const getTargetDuration = () => (
      updatePendingRef.current ? extendedDurationMs : baseDurationMs
    );

    const scheduleFinish = () => {
      window.clearTimeout(finishTimerId);
      const elapsed = performance.now() - startedAt;
      const remaining = Math.max(0, getTargetDuration() - elapsed);
      finishTimerId = window.setTimeout(() => {
        if (!isDone) {
          isDone = true;
          window.cancelAnimationFrame(animationFrameId);
          finishSplash();
        }
      }, remaining);
    };

    const tick = () => {
      const elapsed = performance.now() - startedAt;
      const targetDuration = getTargetDuration();
      const nextProgress = Math.min(99, Math.round((elapsed / targetDuration) * 100));
      const nextScene = prefersReducedMotion ? 'final' : getSceneFromProgress(nextProgress);

      setProgress((previousProgress) => (
        previousProgress === nextProgress ? previousProgress : nextProgress
      ));
      setActiveScene((previousScene) => (
        previousScene === nextScene ? previousScene : nextScene
      ));

      if (!isDone) {
        animationFrameId = window.requestAnimationFrame(tick);
      }
    };

    const handleServiceWorkerUpdate = (event) => {
      updatePendingRef.current = Boolean(event.detail?.pending);
      scheduleFinish();
    };

    window.addEventListener('mpt:sw-update-during-splash', handleServiceWorkerUpdate);
    tick();
    scheduleFinish();

    return () => {
      isDone = true;
      window.cancelAnimationFrame(animationFrameId);
      window.clearTimeout(finishTimerId);
      window.removeEventListener('mpt:sw-update-during-splash', handleServiceWorkerUpdate);
    };
  }, [baseDurationMs, extendedDurationMs, finishSplash, prefersReducedMotion]);

  useEffect(() => {
    if (prefersReducedMotion) return undefined;

    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const context = canvas.getContext('2d');
    let animationFrameId;
    let width = 0;
    let height = 0;
    let particles = [];

    const createParticles = () => {
      const particleCount = Math.min(
        64,
        Math.max(26, Math.round((width * height) / 38000))
      );

      particles = Array.from({ length: particleCount }, () => ({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.32,
        vy: (Math.random() - 0.5) * 0.32,
        radius: Math.random() * 1.8 + 0.7,
        alpha: Math.random() * 0.45 + 0.2,
        hue: Math.random() > 0.48 ? '191, 241, 255' : '211, 103, 255'
      }));
    };

    const resizeCanvas = () => {
      const rect = canvas.getBoundingClientRect();
      const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
      width = rect.width;
      height = rect.height;
      canvas.width = Math.max(1, Math.floor(width * pixelRatio));
      canvas.height = Math.max(1, Math.floor(height * pixelRatio));
      context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
      createParticles();
    };

    const animate = () => {
      context.clearRect(0, 0, width, height);

      particles.forEach((particle, index) => {
        particle.x += particle.vx;
        particle.y += particle.vy;

        if (particle.x < 0 || particle.x > width) particle.vx *= -1;
        if (particle.y < 0 || particle.y > height) particle.vy *= -1;

        for (let nextIndex = index + 1; nextIndex < particles.length; nextIndex += 1) {
          const nextParticle = particles[nextIndex];
          const dx = particle.x - nextParticle.x;
          const dy = particle.y - nextParticle.y;
          const distance = Math.sqrt((dx * dx) + (dy * dy));

          if (distance < 132) {
            context.beginPath();
            context.moveTo(particle.x, particle.y);
            context.lineTo(nextParticle.x, nextParticle.y);
            context.strokeStyle = `rgba(118, 226, 255, ${0.16 * (1 - distance / 132)})`;
            context.lineWidth = 0.75;
            context.stroke();
          }
        }

        context.beginPath();
        context.arc(particle.x, particle.y, particle.radius, 0, Math.PI * 2);
        context.fillStyle = `rgba(${particle.hue}, ${particle.alpha})`;
        context.shadowBlur = 12;
        context.shadowColor = `rgba(${particle.hue}, 0.9)`;
        context.fill();
      });

      animationFrameId = window.requestAnimationFrame(animate);
    };

    resizeCanvas();
    animate();
    window.addEventListener('resize', resizeCanvas);

    return () => {
      window.cancelAnimationFrame(animationFrameId);
      window.removeEventListener('resize', resizeCanvas);
    };
  }, [prefersReducedMotion]);

  if (!isVisible) return null;

  const sceneClassName = (sceneName) => (
    `splash-v3-scene splash-v3-scene--${sceneName}${activeScene === sceneName ? ' is-active' : ''}`
  );

  const splashMarkup = (
    <div
      className={`splash-v3 is-${activeScene}${isExiting ? ' is-exiting' : ''}${prefersReducedMotion ? ' is-reduced-motion' : ''}`}
      role="status"
      aria-live="polite"
      aria-label={statusText}
    >
      <canvas
        ref={canvasRef}
        className="splash-v3-canvas"
        aria-hidden="true"
      />
      <div className="splash-v3-backdrop" aria-hidden="true" />
      <div className="splash-v3-grid" aria-hidden="true" />
      <div className="splash-v3-scanline" aria-hidden="true" />

      <div className="splash-v3-brand-badge" aria-hidden={activeScene === 'brand'}>
        <picture>
          <source srcSet="/images/webp/MPT_Logo2_BLANC.webp" type="image/webp" />
          <img
            src="/images/MPT_Logo2_BLANC.png"
            alt=""
            width="176"
            height="80"
            loading="eager"
          />
        </picture>
      </div>

      <main className="splash-v3-stage">
        <section
          className={sceneClassName('brand')}
          aria-hidden={activeScene !== 'brand'}
        >
          <div className="splash-v3-logo-orbit" aria-hidden="true" />
          <picture className="splash-v3-main-logo">
            <source srcSet="/images/webp/MPT_Logo2_BLANC.webp" type="image/webp" />
            <img
              src="/images/MPT_Logo2_BLANC.png"
              alt="Magic Park Times"
              width="400"
              height="182"
              loading="eager"
            />
          </picture>
          <p className="splash-v3-lead">La visite commence avant les grilles.</p>
        </section>

        <section
          className={sceneClassName('parks')}
          aria-hidden={activeScene !== 'parks'}
        >
          <p className="splash-v3-kicker">Parcs populaires</p>
          <h1>Vos destinations favorites au même endroit.</h1>
          <div className="splash-v3-park-cloud">
            {PARKS.map((park, index) => (
              <span
                key={park}
                className="splash-v3-park-pill"
                style={{ '--index': index }}
              >
                {park}
              </span>
            ))}
          </div>
        </section>

        <section
          className={sceneClassName('live')}
          aria-hidden={activeScene !== 'live'}
        >
          <p className="splash-v3-kicker">Attentes live</p>
          <h1>Temps d&apos;attente en temps réel.</h1>
          <p className="splash-v3-subtitle">Les infos utiles au bon moment.</p>
          <div className="splash-v3-wait-grid">
            {WAIT_TIMES.map((waitTime, index) => (
              <article
                key={waitTime.name}
                className={`splash-v3-wait-card is-${waitTime.tone}`}
                style={{ '--index': index }}
              >
                <span className="splash-v3-wait-name">{waitTime.name}</span>
                <div className="splash-v3-wait-row">
                  <strong>{waitTime.value}</strong>
                  <span>{waitTime.status}</span>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section
          className={sceneClassName('tools')}
          aria-hidden={activeScene !== 'tools'}
        >
          <p className="splash-v3-kicker">Votre visite</p>
          <h1>Météo, cartes, favoris, alertes bientôt.</h1>
          <div className="splash-v3-feature-grid">
            {FEATURE_CARDS.map((feature, index) => (
              <article
                key={feature.label}
                className="splash-v3-feature-card"
                style={{ '--index': index }}
              >
                <span>{feature.label}</span>
                <strong>{feature.title}</strong>
                <small>{feature.meta}</small>
              </article>
            ))}
          </div>
        </section>

        <section
          className={sceneClassName('final')}
          aria-hidden={activeScene !== 'final'}
        >
          <p className="splash-v3-kicker">Magic Park Times</p>
          <h1>Planifiez moins. Profitez plus.</h1>
          <div className="splash-v3-final-grid">
            {FINAL_CARDS.map((card, index) => (
              <article
                key={card.kicker}
                className="splash-v3-final-card"
                style={{ '--index': index }}
              >
                <span>{card.kicker}</span>
                <strong>{card.title}</strong>
                <small>{card.body}</small>
              </article>
            ))}
          </div>
        </section>
      </main>

      <div className="splash-v3-footer">
        <span>{footerMessage}</span>
        <div
          className="splash-v3-progress"
          role="progressbar"
          aria-label="Progression de l’introduction"
          aria-valuemin="0"
          aria-valuemax="100"
          aria-valuenow={progress}
        >
          <span style={{ width: `${progress}%` }} />
        </div>
        <span>{progress}%</span>
      </div>
    </div>
  );

  if (typeof document === 'undefined') {
    return splashMarkup;
  }

  return createPortal(splashMarkup, document.body);
};

export default GeneralSplashScreenV3;
